import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase, getSignedUrl } from "@/lib/supabase";
import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Pencil, Plus, Trash2, User } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

interface DocumentType {
  id: number;
  name: string;
  description: string;
}

export default function RequiredDocuments() {
  const [documents, setDocuments] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [editingDocument, setEditingDocument] = useState<DocumentType | null>(
    null
  );
  const [documentToDelete, setDocumentToDelete] = useState<DocumentType | null>(
    null
  );
  const [relatedDocuments, setRelatedDocuments] = useState<any[]>([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    name: "",
    description: ""
  });

  const { toast } = useToast();

  const fetchDocuments = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("document_types")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast({
        title: "Fehler",
        description: "Dokumente konnten nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const fetchRelatedDocuments = async (docId: number) => {
    setLoadingRelated(true);
    try {
      const { data, error } = await supabase
        .from("documents")
        .select(
          `
          id,
          file_path,
          application_id,
          talent_id,
          talent_profiles (
            given_name,
            family_name,
            profile_picture
          )
        `
        )
        .eq("document_type_id", docId);

      if (error) throw error;

      // Flatten data for the table
      const flattenedData = (data || []).map((doc: any) => {
        const profiles = doc.talent_profiles;
        const profile = Array.isArray(profiles) ? profiles[0] : profiles;
        return {
          ...doc,
          talent_name: profile
            ? `${profile.given_name} ${profile.family_name}`
            : "Unbekannt"
        };
      });

      setRelatedDocuments(flattenedData);

      // Fetch avatar URLs
      const urls: Record<string, string> = {};
      if (data) {
        for (const doc of data) {
          const profiles = doc.talent_profiles;
          const profile = Array.isArray(profiles) ? profiles[0] : profiles;
          if (profile?.profile_picture) {
            try {
              const url = await getSignedUrl(
                "profiles",
                profile.profile_picture
              );
              urls[doc.id] = url;
            } catch (err) {
              console.error("Error fetching avatar:", err);
            }
          }
        }
      }
      setAvatarUrls(urls);
    } catch (error) {
      console.error("Error fetching related documents:", error);
      toast({
        title: "Fehler",
        description: "Zugehörige Dokumente konnten nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setLoadingRelated(false);
    }
  };

  const openCreateDialog = () => {
    setEditingDocument(null);
    setFormData({
      name: "",
      description: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (doc: DocumentType) => {
    setEditingDocument(doc);
    setFormData({
      name: doc.name,
      description: doc.description || ""
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (doc: DocumentType) => {
    setDocumentToDelete(doc);
    setDeleteDialogOpen(true);
    fetchRelatedDocuments(doc.id);
  };

  const handleDocumentDelete = async (docId: number, filePath: string) => {
    try {
      // 1. Delete from Storage
      if (filePath) {
        const { error: storageError } = await supabase.storage
          .from("documents")
          .remove([filePath]);
        if (storageError) console.error("Storage delete error:", storageError);
      }

      // 2. Delete from DB
      const { error } = await supabase
        .from("documents")
        .delete()
        .eq("id", docId);
      if (error) throw error;

      toast({
        title: "Erfolg",
        description: "Dokument wurde gelöscht."
      });

      // 3. Refresh related documents
      if (documentToDelete) {
        fetchRelatedDocuments(documentToDelete.id);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      toast({
        title: "Fehler",
        description: "Dokument konnte nicht gelöscht werden.",
        variant: "destructive"
      });
    }
  };

  const handleSave = async () => {
    try {
      if (editingDocument) {
        // Update existing document type
        const { error } = await supabase
          .from("document_types")
          .update(formData)
          .eq("id", editingDocument.id);

        if (error) throw error;

        toast({
          title: "Erfolg",
          description: "Dokumententyp wurde aktualisiert."
        });
      } else {
        // Create new document type
        const { error } = await supabase
          .from("document_types")
          .insert([formData]);

        if (error) throw error;

        toast({
          title: "Erfolg",
          description: "Dokumententyp wurde erstellt."
        });
      }

      setDialogOpen(false);
      fetchDocuments();
    } catch (error) {
      console.error("Error saving document type:", error);
      toast({
        title: "Fehler",
        description: "Dokumententyp konnte nicht gespeichert werden.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    if (!documentToDelete) return;

    try {
      // 1. If there are related documents, move them to "No type" (ID 0)
      if (relatedDocuments.length > 0) {
        const { error: updateError } = await supabase
          .from("documents")
          .update({ document_type_id: 0 })
          .eq("document_type_id", documentToDelete.id);

        if (updateError) throw updateError;
      }

      // 2. Delete the document type
      const { error } = await supabase
        .from("document_types")
        .delete()
        .eq("id", documentToDelete.id);

      if (error) throw error;

      toast({
        title: "Erfolg",
        description: "Dokumententyp wurde gelöscht."
      });

      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      setRelatedDocuments([]);
      fetchDocuments();
    } catch (error) {
      console.error("Error deleting document type:", error);
      toast({
        title: "Fehler",
        description: "Dokumententyp konnte nicht gelöscht werden.",
        variant: "destructive"
      });
    }
  };

  const columns: ColumnDef<DocumentType>[] = [
    {
      accessorKey: "name",
      header: "Name"
    },
    {
      accessorKey: "description",
      header: "Beschreibung",
      cell: ({ row }) => {
        const description = row.getValue("description") as string;
        return description ? (
          <div className="max-w-[400px] truncate" title={description}>
            {description}
          </div>
        ) : (
          "-"
        );
      }
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => openEditDialog(row.original)}>
              <Pencil className="mr-2 h-4 w-4" />
              Bearbeiten
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={() => openDeleteDialog(row.original)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Löschen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  if (loading) {
    return <div>Lade Dokumententypen...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Erforderliche Dokumente</h2>
          <p className="text-gray-500">
            Verwalte die erforderlichen Dokumententypen im System.
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Neues Dokument
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={documents}
        searchKey="name"
        filterPlaceholder="Dokument suchen..."
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingDocument
                ? "Dokumententyp bearbeiten"
                : "Neuer Dokumententyp"}
            </DialogTitle>
            <DialogDescription>
              {editingDocument
                ? "Bearbeite die Eigenschaften des Dokumententyps."
                : "Erstelle einen neuen erforderlichen Dokumententyp."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="z.B. Lebenslauf, Zeugnis"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Beschreibe, wofür dieses Dokument benötigt wird."
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={!formData.name}>
              {editingDocument ? "Aktualisieren" : "Erstellen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent
          className={relatedDocuments.length > 0 ? "max-w-3xl" : "max-w-md"}
        >
          <DialogHeader>
            <DialogTitle>Dokumententyp löschen</DialogTitle>
            <DialogDescription>
              {relatedDocuments.length > 0
                ? `Dieser Dokumententyp wird noch von ${relatedDocuments.length} Dokumenten verwendet. Das Löschen der einzelnen Dokumente ist nicht zwingend erforderlich.`
                : `Sind Sie sicher, dass Sie "${documentToDelete?.name}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.`}
            </DialogDescription>
          </DialogHeader>

          {loadingRelated ? (
            <div className="py-10 text-center text-sm text-gray-500">
              Überprüfe auf zugehörige Dokumente...
            </div>
          ) : relatedDocuments.length > 0 ? (
            <div className="py-4">
              <DataTable
                data={relatedDocuments}
                searchKey="talent_name"
                filterPlaceholder="Nach Talent suchen..."
                columns={[
                  {
                    id: "type",
                    header: "Kategorie",
                    cell: ({ row }) => (
                      <Badge variant="outline">
                        {row.original.application_id ? "Bewerbung" : "Profil"}
                      </Badge>
                    )
                  },
                  {
                    id: "talent",
                    header: "Talent",
                    cell: ({ row }) => {
                      const avatarUrl = avatarUrls[row.original.id];
                      return (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={avatarUrl} />
                            <AvatarFallback>
                              <User size={14} />
                            </AvatarFallback>
                          </Avatar>
                          <span>{row.original.talent_name}</span>
                        </div>
                      );
                    }
                  },
                  {
                    id: "actions",
                    header: "Aktion",
                    cell: ({ row }) => (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600"
                        onClick={() =>
                          handleDocumentDelete(
                            row.original.id,
                            row.original.file_path
                          )
                        }
                      >
                        <Trash2 size={16} />
                      </Button>
                    )
                  }
                ]}
              />
            </div>
          ) : null}

          {relatedDocuments.length > 0 && (
            <div className="mt-2 text-sm text-amber-600 bg-amber-50 p-3 rounded-md border border-amber-200">
              <p className="font-medium">Hinweis:</p>
              <p>
                Falls Sie den Dokumententyp löschen, ohne die oben gelisteten
                Dokumente zu entfernen, werden diese automatisch in die
                Kategorie &quot;Kein Typ&quot; (ID 0) verschoben.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {relatedDocuments.length > 0
                ? "Dokumententyp trotzdem löschen"
                : "Löschen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
